import React, {
  useCallback, useEffect, useMemo, useState, ReactElement,
} from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useParams, Navigate } from 'react-router';
import FlexContainer from '../../../../components/flex-container/flex-container.component';
import PaddedFlexContainer from '../../../../components/padded-flex-container/padded-flex-container.component';
import Button from '../../../../components/button/button.component';
import Loader from '../../../../components/loader/loader.component';
import TextHeader from '../../../../components/text-header/text-header.component';
import FilteredLink from '../../../../components/filtered-link/filtered-link.component';
import PageError from '../../../../components/page-error/page-error.component';
import TextBlock from '../../../../components/text-block/text-block.component';
import ErrorBlock from '../../../../components/error-block/error-block.component';
import Grid from '../../../../components/grid/grid.component';
import GridItem from '../../../../components/grid-item/grid-item.component';
import Form from '../../../../components/form/form.component';
import FormContainer from '../../../../components/form-container/form-container.component';
import FormFieldSelect from '../../../../components/form-field-select/form-field-select.component';
import FormFieldText from '../../../../components/form-field-text/form-field-text.component';
import FormFieldPhone from '../../../../components/form-field-phone/form-field-phone.component';
import FormFieldPassword from '../../../../components/form-field-password/form-field-password.component';
import FormFieldCheckbox from '../../../../components/form-field-checkbox/form-field-checkbox.component';
import MenuItemList from '../../../../components/menu-item-list/menu-item-list.component';
import RegistrationValidator from './registration.validator';
import MainService from '../../../../services/main-service';
import FormatService from '../../../../services/format-service';
import FormService from '../../../../services/form-service';
import useFormStructureModified from '../../../../hooks/use-form-structure-modified';
import useScreenMobile from '../../../../hooks/use-screen-mobile';
import useServerFormErrors from '../../../../hooks/use-server-form-errors';
import { getUserRegistrationData, saveUserData } from './registration.action';
import { resetFormErrors } from '../../app.action';
import { FORM_ID_REGISTRATION } from '../../../../constants/system/form-ids';
import {
  ROUTE_DASHBOARD, ROUTE_LOGIN, ROUTE_NOT_FOUND, ROUTE_PARAM_HASH,
} from '../../../../constants/system/routes';
import PLATFORM_TITLE_DEFAULT from '../../../../constants/system/platform-titles';

const Registration: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const {
    isDataLoaded,
    currentUser,
    userRegistrationData,
    termsAndConditionsLink,
    roleList,
    form: formProps,
    modified: formModifiedFields,
    handleSubmit: submitForm,
    pristine: isFormPristine,
    isSubmitting: isFormSaving,
    error: formError,
    values: formValues,
    hasValidationErrors: withFormErrors,
    formErrors: serverFormErrors,
    mainFormError: mainServerFormError,
  } = props;

  const {
    change: changeForm,
  } = formProps;

  const routeParams: ReactRouterParams = useParams();
  const isMobileScreen: boolean = useScreenMobile();
  const isFormStructureModified: boolean = useFormStructureModified(formModifiedFields, isDataLoaded);
  const dispatch: ReduxDispatch = useDispatch();

  const actionHashParam: string = routeParams[ROUTE_PARAM_HASH];

  const [isPasswordVisible, setIsPasswordVisible]: [boolean, Function] = useState(false);
  const [isPasswordConfirmVisible, setIsPasswordConfirmVisible]: [boolean, Function] = useState(false);
  const [errorMessage, setErrorMessage]: [string | null, Function] = useState(null);

  const formPrefix: string = 'user';
  const isSaveDisabled: boolean = isFormSaving || (isFormPristine && !isFormStructureModified);
  const platformTitle: string = MainService.PLATFORM_TITLE || PLATFORM_TITLE_DEFAULT;
  const withServerErrors: boolean = !!serverFormErrors && !!Object.keys(serverFormErrors).length;

  const menuItemRoleList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: roleList,
  }), [roleList]);

  const sendForm: Function = useCallback((values: FormValues): Promise<ActionCallback> | null => {
    if (!values) {
      return null;
    }

    return new Promise((resolve: ActionResolveCallback): void => {
      dispatch(saveUserData({
        userForm: {
          confirm: values.confirm,
          user: {
            id: values.user.id,
            firstName: values.user.firstName,
            lastName: values.user.lastName,
            phone: values.user.phone && FormatService.isValidPhoneNumber(values.user.phone, true)
              ? FormatService.formatPhoneValue(values.user.phone) : null,
            password: values.user.password,
            role: values.user.role,
          },
        },
      }, (response: ActionResponse): void => {
        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          submitForm();
        }
      }));
    });
  }, []);

  const onSubmitForm: Function = useCallback((): void => {
    submitForm();

    if (!withFormErrors) {
      sendForm(formValues);
    } else {
      FormService.focusOnFirstFormError();
    }
  }, [withFormErrors, formValues]);

  const onPasswordVisibilityChange: Function = useCallback((value: boolean): void => {
    setIsPasswordVisible(value);
  }, []);

  const onPasswordConfirmVisibilityChange: Function = useCallback((value: boolean): void => {
    setIsPasswordConfirmVisible(value);
  }, []);

  const onPhoneChange: PhoneChangeHandlerFunction = useCallback((phoneValue?: string): void => {
    if (changeForm) {
      changeForm(`${formPrefix}.phone`, phoneValue);
    }
  }, [formPrefix]);

  const onFormChange: Function = useCallback((event: React.SyntheticEvent): void => {
    const fieldName: string = event.target && (event.target as HTMLInputElement).name;

    if (withServerErrors && fieldName) {
      dispatch(resetFormErrors());
      FormService.resetFormServerErrors(formProps, fieldName);
      submitForm();
    }
  }, [withServerErrors, formProps]);

  useEffect((): void => {
    if (actionHashParam) {
      dispatch(getUserRegistrationData({
        actionHash: actionHashParam,
      }, undefined, (exception: ServerError): void => {
        if (exception) {
          setErrorMessage(exception.message);
        }
      }));
    }
  }, [actionHashParam]);

  useServerFormErrors(serverFormErrors, (): void => {
    submitForm();
  });

  if (currentUser && currentUser.id) {
    return <Navigate to={ROUTE_DASHBOARD} />;
  }

  if (!actionHashParam) {
    return <Navigate to={ROUTE_NOT_FOUND} />;
  }

  if (errorMessage) {
    return (
      <PageError isCentered message={errorMessage} />
    );
  }

  if (!isDataLoaded) {
    return <Loader isPageLoader />;
  }

  return (
    <Form withAutocomplete id={FORM_ID_REGISTRATION} onChange={onFormChange}>
      <FlexContainer isCentered>
        <PaddedFlexContainer isLarge isColumn isTextCenter width={900}>
          <Grid withLargeSpacing>
            <GridItem isFullWidth>
              <TextHeader>
                Welcome
              </TextHeader>
              <TextBlock>
                Please fill in your information below to get started
              </TextBlock>
            </GridItem>
          </Grid>
          {
            actionHashParam && userRegistrationData && (
              <PaddedFlexContainer isLarge isColumn isTextLeft width={900}>
                <Grid withLargeSpacing>
                  <GridItem isFullWidth>
                    <TextHeader isSubHeader>
                      Registration Information
                    </TextHeader>
                  </GridItem>
                  <GridItem isFullWidth={isMobileScreen} isMediumWidth={!isMobileScreen}>
                    <FormFieldText
                      isDisabled
                      isOutlined
                      withAutocompleteUserName
                      name={`${formPrefix}.email`}
                      label="Email"
                    />
                  </GridItem>
                  {
                    !isMobileScreen && (
                      <GridItem isMediumWidth />
                    )
                  }
                  <GridItem isFullWidth={isMobileScreen} isMediumWidth={!isMobileScreen}>
                    <FormFieldPassword
                      isOutlined
                      withStrengthBar
                      withAutocompleteNewPassword
                      isVisible={isPasswordVisible}
                      name={`${formPrefix}.password`}
                      label="Password"
                      onVisibilityChange={onPasswordVisibilityChange}
                    />
                  </GridItem>
                  <GridItem isFullWidth={isMobileScreen} isMediumWidth={!isMobileScreen}>
                    <FormFieldPassword
                      isOutlined
                      withNoAutocomplete
                      isVisible={isPasswordConfirmVisible}
                      name={`${formPrefix}.passwordConfirm`}
                      label="Confirm password"
                      onVisibilityChange={onPasswordConfirmVisibilityChange}
                    />
                  </GridItem>
                  <GridItem isFullWidth>
                    <TextHeader isSubHeader>
                      General Information
                    </TextHeader>
                  </GridItem>
                  <GridItem isFullWidth={isMobileScreen} isMediumWidth={!isMobileScreen}>
                    <FormFieldText
                      isOutlined
                      name={`${formPrefix}.firstName`}
                      label="First Name"
                    />
                  </GridItem>
                  <GridItem isFullWidth={isMobileScreen} isMediumWidth={!isMobileScreen}>
                    <FormFieldText
                      isOutlined
                      name={`${formPrefix}.lastName`}
                      label="Last Name"
                    />
                  </GridItem>
                  <GridItem isFullWidth={isMobileScreen} isMediumWidth={!isMobileScreen}>
                    <FormFieldSelect
                      isOutlined
                      isDisabled={roleList && !roleList.length}
                      name={`${formPrefix}.role`}
                      label="Role"
                      optionsTotal={roleList && roleList.length}
                    >
                      {menuItemRoleList}
                    </FormFieldSelect>
                  </GridItem>
                  <GridItem isFullWidth={isMobileScreen} isMediumWidth={!isMobileScreen}>
                    <FormFieldPhone
                      isOutlined
                      withNoPlaceholder
                      name={`${formPrefix}.phone`}
                      label="Phone"
                      onChange={onPhoneChange}
                    />
                  </GridItem>
                  <GridItem isFullWidth>
                    <FormFieldCheckbox name="confirm" />
                    <TextBlock>
                      I agree to the
                      {' '}
                      <FilteredLink isExternal link={termsAndConditionsLink}>
                        terms and conditions
                      </FilteredLink>
                    </TextBlock>
                  </GridItem>
                </Grid>
              </PaddedFlexContainer>
            )
          }
          <Grid withLargeSpacing>
            <GridItem isFullWidth>
              <Button
                isActive
                isDefault
                isExtended
                isDisabled={isSaveDisabled}
                onClick={onSubmitForm}
              >
                Sign Up
              </Button>
            </GridItem>
            <GridItem isFullWidth>
              Already on
              {' '}
              {platformTitle}
              ?
              {' '}
              <FilteredLink link={ROUTE_LOGIN}>
                Sign In
              </FilteredLink>
            </GridItem>
          </Grid>
          {
            (!!formError || !!mainServerFormError) && !isFormSaving && (
              <ErrorBlock errorMessage={formError || mainServerFormError} />
            )
          }
        </PaddedFlexContainer>
      </FlexContainer>
    </Form>
  );
});

const initialValues: FormValues = {};
const mapState: ReduxMapStateCallback = (reducers: Reducers): ReduxMapState => {
  const {
    app,
    registration,
  } = reducers;

  const userData: User | null = registration.registrationData && registration.registrationData.user;

  if (userData) {
    initialValues.confirm = false;
    initialValues.user = {
      id: userData.id,
      firstName: userData.firstName || null,
      lastName: userData.lastName || null,
      email: userData.email || null,
      phone: userData.phone || null,
      role: userData.role || null,
      password: null,
      passwordConfirm: null,
    };
  }

  return {
    isDataLoaded: registration.registrationData && !!registration.registrationData.user,
    currentUser: app.baseData && app.baseData.currentUser,
    roleList: registration.registrationData && registration.registrationData.roleList,
    termsAndConditionsLink: registration.registrationData && registration.registrationData.termsAndConditionsLink,
    userRegistrationData: userData,
  };
};

const RegistrationForm: ReactComponent = (props: ReactComponentProps): ReactElement => {
  const formErrors: ValidationErrors = useSelector(({ registration }: Reducers): ValidationErrorsMap => (
    registration.formErrors
  ));
  const isFormSaving: ValidationErrors = useSelector(({ registration }: Reducers): ValidationErrorsMap => (
    registration.isFormSaving
  ));

  return (
    <FormContainer
      formId={FORM_ID_REGISTRATION}
      component={Registration}
      validator={RegistrationValidator}
      initialValues={initialValues}
      formErrors={formErrors}
      isSubmitting={isFormSaving}
      {...props}
    />
  );
};

export default connect(mapState)(RegistrationForm);
